import { useContext, useEffect } from 'react'
import { UNSAFE_NavigationContext } from 'react-router'
import { CLIENT_WORK_EXIT_EVENT, CLIENT_WORK_EXIT_RESET_EVENT, subscribeClientWorkNavigationGuard, subscribeClientWorkPopGuard } from '../../utils/clientWorkNavigation'

// Studio uses BrowserRouter rather than a data router, so useBlocker is not
// available. Guard its navigator and native history as well as document exits.
export function useClientWorkGuard(when, message = 'Media work is still in progress. Leaving can interrupt uploads or lose the result. Leave this page?') {
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator

  useEffect(() => {
    if (!when) return undefined
    const beforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    let allowNextPop = false
    let scopeExitConfirmed = false
    const confirmLeave = () => {
      if (scopeExitConfirmed) {
        scopeExitConfirmed = false
        return true
      }
      return window.confirm(message)
    }
    const scopeExit = (event) => {
      if (window.confirm(message)) scopeExitConfirmed = true
      else event.preventDefault()
    }
    const resetScopeExit = () => { scopeExitConfirmed = false }
    const removeNavigationGuard = subscribeClientWorkNavigationGuard(navigator, (method) => {
      if (!confirmLeave()) return false
      if (method === 'go') allowNextPop = true
      return true
    })
    const activeIndex = window.history.state?.idx
    let restoring = false
    const popState = (event) => {
      if (allowNextPop) {
        allowNextPop = false
        return
      }
      if (restoring) {
        restoring = false
        event.stopImmediatePropagation()
        return
      }
      if (confirmLeave()) return
      const destinationIndex = event.state?.idx
      if (Number.isInteger(activeIndex) && Number.isInteger(destinationIndex)) {
        event.stopImmediatePropagation()
        const delta = activeIndex - destinationIndex
        if (delta) {
          restoring = true
          window.history.go(delta)
        }
      }
    }
    window.addEventListener('beforeunload', beforeUnload)
    const removePopGuard = subscribeClientWorkPopGuard(popState)
    window.addEventListener(CLIENT_WORK_EXIT_EVENT, scopeExit)
    window.addEventListener(CLIENT_WORK_EXIT_RESET_EVENT, resetScopeExit)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      removePopGuard()
      window.removeEventListener(CLIENT_WORK_EXIT_EVENT, scopeExit)
      window.removeEventListener(CLIENT_WORK_EXIT_RESET_EVENT, resetScopeExit)
      removeNavigationGuard()
    }
  }, [message, navigator, when])
}
